import React, { useEffect, useState } from 'react';
import DefaultModal from 'components/Utils/DefaultModal';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import DefaultButton from 'components/Utils/NewDefaultButton';
import MonacoTest from 'components/Test/MonacoTest';
import Icon from 'components/Utils/NewIcon';
import AIUtils from 'services/AIUtils';
import {
  HISTORY_TYPES,
  METRICS,
  OTHER_HEALTH_RECORDS,
} from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants';
import { StackForm } from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/AppointmentNoteSections';
import ProgressMessage from 'components/Utils/ProgressMessage';
import PerformanceUtils from 'services/PerformanceUtils';
import MenuItem from '@material-ui/core/MenuItem';
import { useStreamingNotePrompts } from 'hooks/useStreamingNotePrompts';

const BeautifyModal = ({
  open,
  onClose,
  patient,
  reason,
  patientHr,
  value,
  onAccept,
  includeNote,
  title = 'Beautify',
}) => {
  return (
    open && (
      <DefaultModal
        className="fit-modal beautify-note-modal"
        padding={false}
        content={
          <BeautifyModalContent
            patient={patient}
            patientHr={patientHr}
            complaint={reason}
            originalNote={value}
            onAccept={onAccept}
            includeNote={includeNote}
          />
        }
        isOpen={open}
        onRequestClose={onClose}
        title={title}
        wrap={true}
      />
    )
  );
};

export const BeautifyModalContent = ({
  patient,
  patientHr,
  originalNote,
  complaint = 'N/A',
  onAccept,
  includeSummary = false,
  includeNote = true,
  buttonsDisabled = false,
}) => {
  const dispatch = useAppDispatch();

  const [oldNote, setOldNote] = useState(originalNote);
  const [shouldBeautify, setShouldBeautify] = useState(true);
  const [records, setRecords] = useState({});
  const [loadingRecords, setLoadingRecords] = useState(false);

  const patientId = patient.id;

  const { generateNote, newNote, setNewNote, loadingNote, filteredStreamingText } =
    useStreamingNotePrompts({
      originalNote: oldNote,
      includeSummary,
      includeNote,
    });

  function onAcceptWrapper(note, records) {
    if (records) {
      let newRecords = PerformanceUtils.clone(records);
      newRecords.metrics = newRecords.metrics.map((metric) => {
        return metric.value;
      });
      records = newRecords;
    }
    return onAccept(note, records);
  }

  useEffect(() => {
    if (!shouldBeautify) {
      return;
    }
    setShouldBeautify(false);
    Promise.all([
      generateNote(
        {
          note: oldNote,
        },
        patientId,
      ),
      generateRecords(oldNote, patientId, patientHr),
    ]);
  }, [dispatch, shouldBeautify, oldNote, complaint, patientHr, patientId]);

  function setRecord(name, value) {
    setRecords((prevState) => {
      return { ...prevState, [name]: value };
    });
  }

  function onChangeRecord(name, value) {
    name = name.split('.')[1];
    return setRecord(name, value);
  }

  async function generateRecords(noteToSend, patientId, patientHr) {
    setLoadingRecords(true);
    setRecords({});
    await Promise.all([
      AIUtils.getAutofilledMetrics(patientId, noteToSend).then((metrics) => {
        setRecord(
          'metrics',
          metrics.map((metric) => {
            return {
              value: {
                name: metric.name,
                value: metric.value,
                label: METRICS.find((it) => it.metric === metric.name)?.label,
              },
            };
          }),
        );
      }),
      AIUtils.getAutofilledMedications(patientId, noteToSend, patientHr).then((value) =>
        setRecord('medications', value),
      ),
      AIUtils.getAutofilledAllergies(patientId, noteToSend, patientHr).then((value) =>
        setRecord('allergies', value),
      ),
      (() => {
        setRecord('active_issues', []);
      })(),
      AIUtils.getAutofilledMedicalIssues(patientId, noteToSend, patientHr).then((value) =>
        setRecord('medical_issues', value),
      ),
      AIUtils.getPrefilledPreventativeScreenings(patientId, noteToSend).then((value) =>
        setRecord('preventative_screenings', value),
      ),
      AIUtils.getAutofilledHistory(
        patientId,
        noteToSend,
        patientHr,
        HISTORY_TYPES.ethnicity_history,
      ).then((value) => setRecord('ethnicity_history', value)),
      AIUtils.getAutofilledHistory(
        patientId,
        noteToSend,
        patientHr,
        HISTORY_TYPES.family_history,
      ).then((value) => setRecord('family_history', value)),
      AIUtils.getAutofilledHistory(
        patientId,
        noteToSend,
        patientHr,
        HISTORY_TYPES.surgical_history,
      ).then((value) => setRecord('surgical_history', value)),
    ]);
    setLoadingRecords(false);
  }

  return (
    <div className="beautify-modal-content">
      <div className="text-areas">
        {includeNote && (
          <div className="monaco-wrapper">
            <span className="small-title">Original Note</span>
            <MonacoTest id="beautify-text-old" value={oldNote} onChange={setOldNote} />
          </div>
        )}
        <div className="monaco-wrapper">
          <span className="small-title">
            Beautified Note
            <Icon
              src="sync-alt"
              className="blue refresh-button"
              loading={loadingNote}
              onClick={() => generateNote({ note: oldNote }, patientId)}
            />
          </span>
          <MonacoTest
            id="beautify-text-new"
            value={loadingNote ? filteredStreamingText : newNote}
            onChange={setNewNote}
          />
        </div>
        <div className="other-sections">
          <Icon
            src="sync-alt"
            className="blue refresh-button"
            loading={loadingRecords}
            onClick={() => generateRecords(newNote, patientId, patientHr)}
          />
          <span className="small-title">Metrics</span>
          {records.metrics ? (
            <StackForm
              healthrecord={{
                id: 'metrics',
                name: 'Metrics',
                fields: ['name', 'value'],
                select: {
                  inputElements: {
                    name: METRICS.map((metric) => (
                      <MenuItem value={metric.metric} key={metric.metric}>
                        {metric.label}
                      </MenuItem>
                    )),
                  },
                },
              }}
              health_records={records}
              onChangeForm={onChangeRecord}
              patient={patient}
              lite={true}
            />
          ) : (
            <ProgressMessage size={15} />
          )}
          <span className="small-title">Allergies</span>
          {records.allergies ? (
            <StackForm
              healthrecord={OTHER_HEALTH_RECORDS.find((record) => record.id === 'allergies')}
              health_records={records}
              onChangeForm={onChangeRecord}
              patient={patient}
              lite={true}
            />
          ) : (
            <ProgressMessage size={15} />
          )}
          <span className="small-title">Active Issues</span>
          {records.active_issues ? (
            <StackForm
              healthrecord={OTHER_HEALTH_RECORDS.find((record) => record.id === 'active_issues')}
              health_records={records}
              onChangeForm={onChangeRecord}
              patient={patient}
              lite={true}
            />
          ) : (
            <ProgressMessage size={15} />
          )}
          <span className="small-title">Medical History</span>
          {records.medical_issues ? (
            <StackForm
              healthrecord={OTHER_HEALTH_RECORDS.find((record) => record.id === 'medical_issues')}
              health_records={records}
              onChangeForm={onChangeRecord}
              patient={patient}
              lite={true}
            />
          ) : (
            <ProgressMessage size={15} />
          )}
          <span className="small-title">Medications</span>
          {records.medications ? (
            <StackForm
              healthrecord={OTHER_HEALTH_RECORDS.find((record) => record.id === 'medications')}
              health_records={records}
              onChangeForm={onChangeRecord}
              patient={patient}
              lite={true}
            />
          ) : (
            <ProgressMessage size={15} />
          )}
          {Object.keys(HISTORY_TYPES).map((type) => (
            <>
              <span className="small-title">{HISTORY_TYPES[type]}</span>
              {records[type] ? (
                <StackForm
                  healthrecord={OTHER_HEALTH_RECORDS.find((record) => record.id === type)}
                  health_records={records}
                  onChangeForm={onChangeRecord}
                  patient={patient}
                  lite={true}
                  hideFields={['notes']}
                />
              ) : (
                <ProgressMessage size={15} />
              )}
            </>
          ))}
          <span className="small-title">Preventive Screenings</span>
          {records.preventative_screenings ? (
            <StackForm
              healthrecord={OTHER_HEALTH_RECORDS.find(
                (record) => record.id === 'preventative_screenings',
              )}
              health_records={records}
              onChangeForm={onChangeRecord}
              patient={patient}
              lite={true}
            />
          ) : (
            <ProgressMessage size={15} />
          )}
        </div>
      </div>
      <div className="buttons">
        <DefaultButton
          name="Accept Note"
          onClick={() => onAcceptWrapper(newNote, undefined)}
          disabled={loadingNote || buttonsDisabled}
          hide={!includeNote && !includeSummary}
        />
        <DefaultButton
          name="Accept Records"
          onClick={() => onAcceptWrapper(undefined, records)}
          disabled={loadingRecords || buttonsDisabled}
        />
        <DefaultButton
          name="Accept Note + Records"
          className="blue"
          onClick={() => onAcceptWrapper(newNote, records)}
          style={{ width: '200px' }}
          disabled={loadingNote || loadingRecords || buttonsDisabled}
          hide={!includeNote && !includeSummary}
        />
      </div>
    </div>
  );
};

export default BeautifyModal;
